import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { movies } from "@/mocks/movies";
import type { Movie } from "@/types/content";

const featuredMovies: Movie[] = movies.filter((movie) => movie.featured && movie.still);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = featuredMovies.length;

  const goTo = useCallback(
    (next: number) => {
      setIndex((next + total) % total);
    },
    [total],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % total);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [total]);

  const current = featuredMovies[index];

  return (
    <section
      className="relative w-full overflow-hidden bg-background-950"
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) {
          goTo(delta > 0 ? index - 1 : index + 1);
        }
        touchStartX.current = null;
      }}
    >
      <div className="absolute inset-0">
        {featuredMovies.map((movie, position) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              position === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={movie.still}
              alt={`${movie.title}剧照`}
              className="h-full w-full object-cover object-center"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/85 to-background-950/30"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/20 to-background-950/50"></div>
      </div>

      <div className="relative mx-auto flex min-h-[640px] w-full max-w-[1440px] flex-col justify-between px-4 pb-10 pt-28 md:min-h-[86vh] md:px-12 md:pb-14 md:pt-36">
        <div className="max-w-2xl animate-fade-up">
          <p className="flex flex-wrap items-center gap-2 font-label text-xs uppercase tracking-[0.3em] text-accent-400">
            <span className="h-px w-8 bg-accent-500"></span>
            Japan Ethics Archive
          </p>
          <h1 className="mt-5 text-3xl font-semibold leading-[1.25] tracking-tight text-foreground-950 md:text-5xl">
            在日本伦理的光影里
            <br />
            读懂人性与<span className="text-accent-400">边界</span>
          </h1>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            专业影视资讯、详实剧情解析与作品档案。我们以编辑的视角整理日本伦理题材影视作品，帮助你在庞杂的片单中找到真正值得细读的那一部。
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to="/category"
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm text-foreground-950 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-list-line" aria-hidden="true"></i>
              <span className="whitespace-nowrap">浏览作品档案</span>
            </Link>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 rounded-md border border-background-600 px-5 py-3 text-sm text-foreground-800 transition-colors hover:border-accent-500 hover:text-accent-400"
            >
              <i className="ri-article-line" aria-hidden="true"></i>
              <span className="whitespace-nowrap">阅读深度解析</span>
            </Link>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <div className="flex items-center gap-3 text-xs text-foreground-500">
              <span className="font-label text-2xl leading-none text-accent-500">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span className="h-px w-8 bg-background-600"></span>
              <span className="font-label tracking-[0.2em]">
                / {String(total).padStart(2, "0")} 本周焦点
              </span>
            </div>
            <Link to={`/detail/${current.id}`} className="group mt-3 block">
              <h2 className="text-xl font-medium text-foreground-950 transition-colors group-hover:text-accent-400 md:text-2xl">
                {current.title}
                <span className="ml-3 font-label text-sm tracking-[0.12em] text-foreground-500">
                  {current.year}
                </span>
              </h2>
              <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                {current.summary}
              </p>
              <span className="mt-3 inline-flex items-center gap-2 text-sm text-accent-400">
                查看作品详情
                <i
                  className="ri-arrow-right-line transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                ></i>
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {featuredMovies.map((movie, position) => (
                <button
                  key={movie.id}
                  type="button"
                  onClick={() => goTo(position)}
                  aria-label={`切换到 ${movie.title}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    position === index
                      ? "w-8 bg-accent-500"
                      : "w-4 bg-background-600 hover:bg-background-500"
                  }`}
                ></button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => goTo(index - 1)}
                className="flex h-10 w-10 items-center justify-center rounded-md border border-background-600 text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-400"
                aria-label="上一个焦点内容"
              >
                <i className="ri-arrow-left-line" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                onClick={() => goTo(index + 1)}
                className="flex h-10 w-10 items-center justify-center rounded-md border border-background-600 text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-400"
                aria-label="下一个焦点内容"
              >
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}